import { memo, useCallback } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { DATE_ID_FORMAT, formatHoursToTimeString } from '@/helpers';
import { cn } from '@/lib/utils';

import { getTimelineCellColorScale } from '../../helpers';
import { calculateCenterTopPosition } from '../../helpers/tooltip-helpers';
import { UtilizationBasedOn } from '../../types/utilization-based-on';
import { usePlannerTooltip } from '../tooltip/planner-tooltip-provider';

export const TimelineCell = memo(
  ({
    date,
    cellWidth,
    utilizationPercentage = 0,
    assigned = 0,
    capacity = 0,
    onDemand = false,
    isCompact,
    height,
    disabled,
    utilizationBasedOn,
  }: {
    date: dayjs.Dayjs;
    cellWidth: number;
    utilizationPercentage?: number;
    assigned?: number;
    capacity?: number;
    onDemand?: boolean;
    isCompact: boolean;
    height: number;
    utilizationBasedOn: UtilizationBasedOn;
    disabled?: boolean;
  }) => {
    const { t } = useTranslation('authenticated.planner');
    const { showTooltip, hideTooltip } = usePlannerTooltip();
    const isWeekend = date.day() === 0 || date.day() === 6;
    const isToday = date.isSame(dayjs(), 'day');

    const color = getTimelineCellColorScale(utilizationPercentage);

    const isLastDayOfMonth = date.endOf('month').isSame(date, 'day');

    const tooltipContent = onDemand
      ? t('userInfo.onDemand')
      : t(
          `userInfo.tooltip.utilizationCell.${utilizationBasedOn === UtilizationBasedOn.BillableCapacity ? 'billable' : 'total'}`,
          {
            utilizationPercent: utilizationPercentage.toFixed(0),
            assigned: formatHoursToTimeString(assigned / 60),
            capacity: formatHoursToTimeString(capacity / 60),
          }
        );

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isWeekend && !disabled) {
          const { centerX, topY } = calculateCenterTopPosition(e.currentTarget);
          showTooltip(tooltipContent, centerX, topY);
        }
      },
      [isWeekend, disabled, tooltipContent, showTooltip]
    );

    const handleMouseLeave = useCallback(() => {
      hideTooltip();
    }, [hideTooltip]);

    return (
      <div
        className={cn('relative overflow-hidden border-b border-r', {
          'pointer-events-none bg-border/60': disabled,
          'pointer-events-none bg-muted/50': isWeekend && !disabled,
          'border-r-2': isLastDayOfMonth,
          'bg-success/10 dark:bg-success/20': isToday,
        })}
        id={`cell-${date.format(DATE_ID_FORMAT)}`}
        style={{
          width: `${cellWidth}rem`,
          minWidth: `${cellWidth}rem`,
          height,
        }}
      >
        {!isWeekend &&
          !disabled &&
          (isCompact ? (
            <div
              className="pointer-events-auto relative z-[5] flex h-fit w-full items-center justify-center pt-2"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className={`h-2 w-2 rounded-full `} style={{ background: color }} />
            </div>
          ) : (
            <div
              className="pointer-events-auto relative z-[5] h-fit w-full border-b py-1 text-center text-[10px]"
              style={{ color }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {onDemand
                ? t('userInfo.onDemandShort')
                : t('userInfo.percentage', {
                    value: utilizationPercentage.toFixed(0),
                  })}
            </div>
          ))}
      </div>
    );
  }
);
